import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, MessageCircle, Phone, MapPin, Crosshair } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { useLang } from "@/context/LanguageContext";
import { useData } from "@/context/DataContext";
import { PHONE_TEL, openWa, WA_BASE } from "@/lib/wa";

const STATUS_STYLE = {
  available: "bg-[#10B981]/15 text-[#34d399] border-[#10B981]/40",
  after_confirmation: "bg-gold/10 text-gold border-gold/40",
  unavailable: "bg-[#EF4444]/10 text-[#f87171] border-[#EF4444]/40",
};

export default function Riyadh() {
  const { t, pick, lang } = useLang();
  const { zones, neighborhoods } = useData();
  const [q, setQ] = useState("");
  const [zone, setZone] = useState("");
  const [locState, setLocState] = useState("");

  const hoodName = (n) => pick(n.name_ar, n.name_en);

  const list = useMemo(() => {
    return (neighborhoods || []).filter((n) => {
      const okZone = !zone || n.zone === zone;
      const needle = q.trim().toLowerCase();
      const okQ = !needle || n.name_ar.includes(q.trim()) || n.name_en.toLowerCase().includes(needle) || n.slug.includes(needle);
      return okZone && okQ;
    });
  }, [neighborhoods, q, zone]);

  const zoneName = (id) => pick((zones.find((z) => z.id === id) || {}).ar, (zones.find((z) => z.id === id) || {}).en) || id;

  const shareLocation = () => {
    if (!navigator.geolocation) return setLocState("err");
    setLocState("loading");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocState("ok");
        const msg = `${t("riyadh.waMsg").replace("{hood}", "____").replace("{service}", "____")}\nموقعي: https://maps.google.com/?q=${pos.coords.latitude},${pos.coords.longitude}`;
        window.open(`${WA_BASE}?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
      },
      () => setLocState("err"),
      { timeout: 10000 }
    );
  };

  return (
    <main>
      <SEO title="بنشر متنقل في جميع أحياء الرياض | ميرو للإطارات وخدمات السيارات" description={t("riyadh.sub")} path="/riyadh" />
      <PageHero eyebrow="RIYADH COVERAGE" title={t("riyadh.title")} sub={t("riyadh.sub")} />

      <section className="pb-20 sm:pb-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center">
              <div className="relative flex-1">
                <Search size={18} className="absolute top-1/2 -translate-y-1/2 text-dim" style={{ insetInlineStart: "1rem" }} />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={t("riyadh.searchPh")}
                  className="field !ps-11"
                  data-testid="riyadh-search-input"
                  aria-label={t("riyadh.searchPh")}
                />
              </div>
              <button onClick={shareLocation} className="btn-ghost !py-3" data-testid="riyadh-share-location">
                <Crosshair size={16} /> {locState === "loading" ? t("order.gettingLoc") : t("riyadh.shareLoc")}
              </button>
            </div>
            {locState === "err" && <p className="mb-6 text-xs text-[#f87171]">{t("order.locError")}</p>}

            <div className="mb-10 flex flex-wrap gap-2.5" data-testid="riyadh-zone-filters">
              <button onClick={() => setZone("")} className={`rounded-full px-4 py-2 text-sm font-bold transition-all ${!zone ? "bg-gold text-ink" : "border border-white/12 bg-coal/60 text-zinc-300 hover:border-gold/50"}`} data-testid="zone-filter-all">
                {t("riyadh.filterAll")}
              </button>
              {zones.map((z) => (
                <button
                  key={z.id}
                  onClick={() => setZone(z.id)}
                  className={`rounded-full px-4 py-2 text-sm font-bold transition-all ${zone === z.id ? "bg-gold text-ink" : "border border-white/12 bg-coal/60 text-zinc-300 hover:border-gold/50"}`}
                  data-testid={`zone-filter-${z.id}`}
                >
                  {pick(z.ar, z.en)}
                </button>
              ))}
            </div>
          </Reveal>

          {list.length === 0 && (
            <div className="rounded-3xl border border-white/10 bg-coal/60 p-10 text-center">
              <p className="text-mist">{t("riyadh.none")}</p>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="riyadh-neighborhoods-grid">
            {list.map((n, i) => (
              <Reveal key={n.slug} delay={i % 3}>
                <div className="group flex h-full flex-col rounded-3xl border border-white/10 bg-coal/60 p-6 transition-all duration-300 hover:border-gold/35" data-testid={`hood-card-${n.slug}`}>
                  <div className="mb-3 flex items-start justify-between gap-3">
                    {n.status !== "unavailable" ? (
                      <Link to={`/neighborhood/${n.slug}`} data-testid={`hood-page-link-${n.slug}`} className="text-lg font-bold text-white transition-colors group-hover:text-gold">
                        {pick("حي", "")} {hoodName(n)}
                      </Link>
                    ) : (
                      <h3 className="text-lg font-bold text-white">{pick("حي", "")} {hoodName(n)}</h3>
                    )}
                    <span className={`shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-bold ${STATUS_STYLE[n.status] || STATUS_STYLE.after_confirmation}`} data-testid={`hood-status-${n.slug}`}>
                      {t(`status.${n.status}`)}
                    </span>
                  </div>
                  <p className="mb-5 flex-1 text-xs text-dim">{zoneName(n.zone)}</p>
                  <div className="flex gap-2.5">
                    <button
                      onClick={() => openWa(t("riyadh.waMsg").replace("{hood}", hoodName(n)).replace("{service}", lang === "ar" ? "____" : "____"))}
                      className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-[#1FAF5A]/15 py-2.5 text-xs font-bold text-[#2ecc71] transition-all hover:bg-[#1FAF5A]/25"
                      data-testid={`hood-wa-${n.slug}`}
                    >
                      <MessageCircle size={13} /> {t("riyadh.orderWa")}
                    </button>
                    <a href={PHONE_TEL} className="flex items-center justify-center gap-1.5 rounded-full border border-gold/40 px-4 py-2.5 text-xs font-bold text-gold transition-all hover:bg-gold/10" data-testid={`hood-call-${n.slug}`}>
                      <Phone size={13} /> {t("common.call")}
                    </a>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-12">
            <div className="rounded-3xl border border-gold/20 bg-coal/50 p-6">
              <h2 className="mb-4 text-base font-bold text-white">الأكثر بحثاً في الرياض</h2>
              <div className="flex flex-wrap gap-2.5">
                {[
                  ["tire-change-riyadh", "تغيير كفرات متنقل"],
                  ["puncture-riyadh", "إصلاح بنشر متنقل"],
                  ["balancing-riyadh", "ترصيص كفرات متنقل"],
                  ["battery-riyadh", "تغيير بطاريات متنقل"],
                  ["tpms-riyadh", "برمجة حساسات الكفرات"],
                ].map(([to, label]) => (
                  <Link key={to} to={`/${to}`} className="rounded-full border border-white/12 bg-ink/70 px-4 py-2 text-sm text-zinc-300 transition-all hover:border-gold/50 hover:text-gold" data-testid={`popular-search-${to}`}>
                    {label}
                  </Link>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal className="mt-8">
            <div className="flex items-start gap-3 rounded-3xl border border-gold/20 bg-coal/50 p-6">
              <MapPin size={20} className="mt-0.5 shrink-0 text-gold" />
              <p className="text-sm leading-8 text-mist">{t("status.note")}</p>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
